import type {
  ExceptionStackTrace,
  ReportRequest,
  CollectionFrame,
  SessionPayload,
  SessionRecordingPayload,
  LogEvent,
  NetworkEvent,
  NavigationEvent,
  CustomEvent,
  TracewayEvent,
} from "@tracewayapp/core";
import { parseConnectionString, generateUUID, EventBuffer, nowISO } from "@tracewayapp/core";
import { sendReport } from "./transport.js";
import { SessionRecorder } from "./session-recorder.js";
import { SessionLifecycle } from "./session-lifecycle.js";
import { collectDefaultAttributes } from "./default-attributes.js";
import { debugIdsForStackTrace } from "./debug-ids.js";

interface RrwebLikeEvent {
  timestamp?: number;
}

function epochMsToISO(ms: number): string {
  return new Date(ms).toISOString();
}

/**
 * Computes the wall-clock window covered by a recording. Prefers rrweb events
 * (each carries a `timestamp` epoch ms); falls back to the timestamp range of
 * the buffered logs/actions when there's no replay data — that path is what
 * fires when `sessionRecording: false` but logs/actions are still being kept.
 */
function computeRecordingAnchors(
  recorderEvents: unknown[],
  logs: LogEvent[],
  actions: Array<NetworkEvent | NavigationEvent | CustomEvent>,
): { startedAt?: string; endedAt?: string } {
  if (recorderEvents.length > 0) {
    const stamps: number[] = [];
    for (const e of recorderEvents) {
      const ts = (e as RrwebLikeEvent)?.timestamp;
      if (typeof ts === "number" && Number.isFinite(ts)) stamps.push(ts);
    }
    if (stamps.length > 0) {
      return {
        startedAt: epochMsToISO(Math.min(...stamps)),
        endedAt: epochMsToISO(Math.max(...stamps)),
      };
    }
  }

  const eventStamps = [
    ...logs.map((e) => Date.parse(e.timestamp)),
    ...actions.map((e) => Date.parse(e.timestamp)),
  ].filter((n) => Number.isFinite(n));
  if (eventStamps.length === 0) return {};
  return {
    startedAt: epochMsToISO(Math.min(...eventStamps)),
    endedAt: epochMsToISO(Math.max(...eventStamps)),
  };
}

export const DEFAULT_IGNORE_PATTERNS: Array<string | RegExp> = [
  // Network errors (browser-specific messages)
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
  "Network Error",
  // RN-style network error (surfaces here when an app shares code with React Native)
  "Network request failed",
  // Timeout / Abort
  "The operation was aborted",
  /timeout/i,
  // 4xx HTTP errors (common library patterns)
  /status code 4\d{2}/,
  /failed: 4\d{2}/,
];

export interface TracewayFrontendOptions {
  debug?: boolean;
  debounceMs?: number;
  retryDelayMs?: number;
  version?: string;
  sessionRecording?: boolean;
  sessionRecordingSegmentDuration?: number;
  /**
   * Upload every segment of the session regardless of whether an exception
   * fires. Each segment (default ~30 s, see `sessionRecordingSegmentDuration`)
   * becomes its own `session_recordings` row on the backend, all linked to a
   * parent `sessions` row by `sessionId`.
   *
   *   - Inactivity timeout: 15 min ends the session.
   *   - Max duration: 60 min ends the session.
   *   - `pagehide` ends the session and triggers a final flush.
   *
   * Defaults to false to preserve the existing exception-only behaviour.
   */
  recordAllSessions?: boolean;
  ignoreErrors?: Array<string | RegExp>;
  beforeCapture?: (exception: ExceptionStackTrace) => boolean;
  /** Mirror console.{log,info,warn,error,debug} into the rolling log buffer. Default true. */
  captureLogs?: boolean;
  /** Record fetch / XHR requests as network actions. Default true. */
  captureNetwork?: boolean;
  /** Record History API push/replace/pop as navigation actions. Default true. */
  captureNavigation?: boolean;
  /** Window kept in the rolling log/action buffers. Default 10_000ms. */
  eventsWindowMs?: number;
  /** Hard cap applied independently to logs and actions. Default 200. */
  eventsMaxCount?: number;
  /**
   * When `true`, every `fetch` / `XHR` response with `status >= 500` is also
   * reported to Traceway as a synthetic exception (in addition to the network
   * action it already records). 4xx responses are intentionally not captured
   * by this flag — see `DEFAULT_IGNORE_PATTERNS`. Default `false`.
   */
  captureHttpServerErrors?: boolean;
}

export class TracewayFrontendClient {
  private apiUrl: string;
  private token: string;
  private debug: boolean;
  private debounceMs: number;
  private retryDelayMs: number;
  private version: string;

  private pendingExceptions: ExceptionStackTrace[] = [];
  private pendingRecordings: SessionRecordingPayload[] = [];
  private pendingSessions: SessionPayload[] = [];
  private isSyncing = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private recorder: SessionRecorder | null = null;
  private lifecycle: SessionLifecycle | null = null;
  private readonly recordAllSessions: boolean;
  private sessionId: string | null = null;
  private sessionStartedAt: string | null = null;
  private segmentIndex = 0;
  /**
   * App-defined attributes attached to every session and exception emitted by
   * this client. Set via setAttribute() / setAttributes(). Auto-collected
   * defaults take a back seat to these; per-call exception attributes win
   * over both.
   */
  private globalAttributes: Record<string, string> = {};
  /**
   * Set true once the page begins unloading (`pagehide`). Forces the next
   * sync to dispatch via `fetch(..., { keepalive: true })` with a raw JSON
   * body so the closing-session payload survives the navigation.
   */
  private unloading = false;

  private ignoreErrors: Array<string | RegExp>;
  private beforeCapture:
    | ((exception: ExceptionStackTrace) => boolean)
    | null;

  readonly captureLogs: boolean;
  readonly captureNetwork: boolean;
  readonly captureNavigation: boolean;
  readonly captureHttpServerErrors: boolean;
  private readonly logs: EventBuffer<LogEvent>;
  private readonly actions: EventBuffer<NetworkEvent | NavigationEvent | CustomEvent>;

  constructor(connectionString: string, options: TracewayFrontendOptions = {}) {
    const { token, apiUrl } = parseConnectionString(connectionString);
    this.apiUrl = apiUrl;
    this.token = token;
    this.debug = options.debug ?? false;
    this.debounceMs = options.debounceMs ?? 1500;
    this.retryDelayMs = options.retryDelayMs ?? 10000;
    this.version = options.version ?? "";
    this.ignoreErrors = options.ignoreErrors ?? DEFAULT_IGNORE_PATTERNS;
    this.beforeCapture = options.beforeCapture ?? null;

    this.captureLogs = options.captureLogs ?? true;
    this.captureNetwork = options.captureNetwork ?? true;
    this.captureNavigation = options.captureNavigation ?? true;
    this.captureHttpServerErrors = options.captureHttpServerErrors ?? false;

    // When always-on session recording is on, segments rotate every 30 s and
    // drain the logs/actions buffers on each rotation. The window/cap need to
    // span a full segment so we don't ship a partial slice. When always-on is
    // off, keep the legacy 10 s / 200 cap — the rolling buffer powers the
    // exception-bound clip and shouldn't grow unbounded.
    const alwaysOn = options.recordAllSessions === true;
    const bufferOpts = {
      windowMs: options.eventsWindowMs ?? (alwaysOn ? 30_000 : 10_000),
      maxSize: options.eventsMaxCount ?? (alwaysOn ? 600 : 200),
    };
    this.logs = new EventBuffer<LogEvent>(bufferOpts);
    this.actions = new EventBuffer<NetworkEvent | NavigationEvent | CustomEvent>(bufferOpts);

    this.recordAllSessions = options.recordAllSessions ?? false;
    const hasWindow = typeof window !== "undefined";

    // The always-on session/lifecycle path runs even when sessionRecording is
    // disabled — exceptions still get stamped with sessionId and the parent
    // sessions row is still created. Without a recorder, no rrweb segments
    // ride along, but the linkage in the dashboard remains intact.
    if (this.recordAllSessions && hasWindow) {
      this.beginSession();
      this.lifecycle = new SessionLifecycle({
        onUnloading: () => {
          this.unloading = true;
        },
        onSessionEnd: () => this.endSession(),
        onSessionRestart: () => this.restartSession(),
        onSoftFlush: () => {
          if (this.pendingSessions.length > 0 || this.pendingRecordings.length > 0) {
            this.scheduleSync();
          }
        },
      });
      this.lifecycle.install();
    }

    if (options.sessionRecording !== false && hasWindow) {
      const recorderOptions: ConstructorParameters<typeof SessionRecorder>[0] = {
        segmentDuration: options.sessionRecordingSegmentDuration,
      };
      if (this.recordAllSessions) {
        recorderOptions.onSegmentReady = (seg) => this.handleSegmentReady(seg);
        // rrweb's emit fires for every DOM mutation/input event — use it as
        // the lifecycle's activity heartbeat so we don't double-listen on
        // window for mousedown/keydown/scroll/etc.
        recorderOptions.onActivity = () => this.lifecycle?.markActivity();
      }
      this.recorder = new SessionRecorder(recorderOptions);
      this.recorder.start();
    }
  }

  // ── Session lifecycle (always-on) ──────────────────────────────────────

  private beginSession(): void {
    this.sessionId = generateUUID();
    this.sessionStartedAt = nowISO();
    this.segmentIndex = 0;
    this.pendingSessions.push({
      id: this.sessionId,
      startedAt: this.sessionStartedAt,
      attributes: this.composedSessionAttributes(),
    });
    this.scheduleSync();
  }

  /**
   * Merge browser defaults with whatever app-level scope was set via
   * setAttribute(). App attrs override defaults on key collision.
   */
  private composedSessionAttributes(): Record<string, string> {
    return {
      ...collectDefaultAttributes(),
      ...this.globalAttributes,
    };
  }

  // ── Global scope ────────────────────────────────────────────────────────

  /**
   * Attach a key/value attribute to every subsequent session and exception
   * emitted by this client. If a session is already open, its attributes are
   * refreshed on the backend immediately. Setting the same key replaces the
   * previous value.
   */
  setAttribute(key: string, value: string): void {
    if (!key) return;
    this.globalAttributes[key] = value;
    this.refreshOpenSessionAttributes();
  }

  /**
   * Bulk version of setAttribute. Caller's keys override existing scope.
   * Triggers one session refresh after the merge, not one per key.
   */
  setAttributes(attrs: Record<string, string>): void {
    if (!attrs) return;
    let changed = false;
    for (const k of Object.keys(attrs)) {
      if (!k) continue;
      this.globalAttributes[k] = attrs[k]!;
      changed = true;
    }
    if (changed) this.refreshOpenSessionAttributes();
  }

  removeAttribute(key: string): void {
    if (key in this.globalAttributes) {
      delete this.globalAttributes[key];
      this.refreshOpenSessionAttributes();
    }
  }

  clearAttributes(): void {
    if (Object.keys(this.globalAttributes).length === 0) return;
    this.globalAttributes = {};
    this.refreshOpenSessionAttributes();
  }

  /** @internal — exposed for tests. */
  currentAttributes(): Record<string, string> {
    return { ...this.globalAttributes };
  }

  /**
   * When the global scope changes mid-session, push a session-refresh
   * payload (no endedAt) so the backend's ON CONFLICT update writes the new
   * attribute blob into the existing row. Without this the new attrs would
   * only land at session close.
   */
  private refreshOpenSessionAttributes(): void {
    if (!this.sessionId || !this.sessionStartedAt) return;
    if (this.recordAllSessions !== true) return;
    this.pendingSessions.push({
      id: this.sessionId,
      startedAt: this.sessionStartedAt,
      attributes: this.composedSessionAttributes(),
    });
    this.scheduleSync();
  }

  private endSession(): void {
    if (!this.sessionId || !this.sessionStartedAt) return;

    if (this.recorder) {
      const drained = this.recorder.drainCurrent();
      if (drained && drained.events.length > 0) {
        this.queueSegment(drained);
      }
    }

    this.pendingSessions.push({
      id: this.sessionId,
      startedAt: this.sessionStartedAt,
      endedAt: nowISO(),
      // Re-snapshot attributes so the upsert doesn't clobber the opening
      // attribute blob with an empty map. Also picks up URL changes and any
      // global-scope attributes set during the session.
      attributes: this.composedSessionAttributes(),
    });

    // On unload paths the debounce timer never fires — flush directly so the
    // closing payload rides out on fetch keepalive.
    if (this.unloading) {
      if (this.debounceTimer !== null) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      void this.doSync();
    } else {
      this.scheduleSync();
    }
  }

  /**
   * Begin a fresh session after the page came back from bfcache. The previous
   * session was closed by `pagehide`; we generate a new sessionId and reset
   * the unloading flag so subsequent syncs go back to the gzipped path.
   */
  private restartSession(): void {
    this.unloading = false;
    this.beginSession();
  }

  private handleSegmentReady(segment: { events: unknown[]; startedAt: string; endedAt: string }): void {
    if (!this.sessionId) return;
    this.queueSegment(segment);
    this.scheduleSync();
  }

  private queueSegment(segment: { events: unknown[]; startedAt: string; endedAt: string }): void {
    if (!this.sessionId) return;
    // Drain the rolling logs/actions buffers and attach them to this segment.
    // Clearing prevents segment N+1 from re-shipping the same entries.
    const logs = this.logs.snapshot();
    const actions = this.actions.snapshot();
    if (logs.length > 0) this.logs.clear();
    if (actions.length > 0) this.actions.clear();

    const payload: SessionRecordingPayload = {
      sessionId: this.sessionId,
      segmentIndex: this.segmentIndex++,
      events: segment.events,
      startedAt: segment.startedAt,
      endedAt: segment.endedAt,
    };
    if (logs.length > 0) payload.logs = logs;
    if (actions.length > 0) payload.actions = actions;
    this.pendingRecordings.push(payload);
  }

  /** @internal — exposed for tests. */
  currentSessionId(): string | null {
    return this.sessionId;
  }

  // ── Timeline event recording ────────────────────────────────────────────

  recordLog(level: LogEvent["level"], message: string): void {
    if (!this.captureLogs) return;
    this.logs.add({ type: "log", timestamp: nowISO(), level, message });
  }

  recordNetworkEvent(event: Omit<NetworkEvent, "type" | "timestamp"> & { timestamp?: string }): void {
    if (!this.captureNetwork) return;
    // Don't record the SDK's own report uploads — otherwise every segment
    // flush would be captured as a "network action" and end up in the next
    // segment's actions buffer, creating a self-referential tail.
    if (this.isOwnReportUrl(event.url)) return;
    this.actions.add({
      type: "network",
      timestamp: event.timestamp ?? nowISO(),
      method: event.method,
      url: event.url,
      durationMs: event.durationMs,
      statusCode: event.statusCode,
      requestBytes: event.requestBytes,
      responseBytes: event.responseBytes,
      error: event.error,
    });
  }

  private isOwnReportUrl(url: string): boolean {
    if (!url) return false;
    if (url === this.apiUrl) return true;
    // Defensive: match URLs that start with apiUrl plus a query/fragment
    // suffix, in case the transport ever appends one for diagnostics.
    if (url.startsWith(this.apiUrl + "?") || url.startsWith(this.apiUrl + "#")) return true;
    return false;
  }

  recordNavigationEvent(event: Omit<NavigationEvent, "type" | "timestamp"> & { timestamp?: string }): void {
    if (!this.captureNavigation) return;
    this.actions.add({
      type: "navigation",
      timestamp: event.timestamp ?? nowISO(),
      action: event.action,
      from: event.from,
      to: event.to,
    });
  }

  /**
   * Records a custom user-defined breadcrumb. Use to log any app-level action
   * that should ride along with the next exception ("user_tapped_pay",
   * "cart_synced", etc.). Always recorded — there is no per-category opt-out.
   */
  recordAction(category: string, name: string, data?: Record<string, unknown>): void {
    this.actions.add({
      type: "custom",
      timestamp: nowISO(),
      category,
      name,
      data,
    });
  }

  /** @internal — exposed for tests. */
  bufferedLogs(): LogEvent[] {
    return this.logs.snapshot();
  }

  /** @internal — exposed for tests. */
  bufferedActions(): TracewayEvent[] {
    return this.actions.snapshot();
  }

  // ── Exception lifecycle ─────────────────────────────────────────────────

  /**
   * Promote a 5xx HTTP response into a captured exception. Called from the
   * fetch / XHR wrappers when `captureHttpServerErrors` is enabled.
   */
  captureHttpServerError(
    method: string,
    url: string,
    statusCode: number,
  ): void {
    this.addException({
      traceId: null,
      stackTrace: `HTTP ${statusCode} ${method} ${url}`,
      recordedAt: nowISO(),
      attributes: {
        "http.method": method,
        "http.url": url,
        "http.status_code": String(statusCode),
      },
      isMessage: true,
    });
  }

  addException(exception: ExceptionStackTrace): void {
    if (this.shouldIgnore(exception)) {
      if (this.debug) {
        console.debug(
          "Traceway: exception suppressed by filter",
          exception.stackTrace.slice(0, 120),
        );
      }
      return;
    }

    // Merge in browser-context defaults plus the global scope. Caller-
    // supplied keys win over both. Order matters: defaults < global < caller.
    exception.attributes = {
      ...collectDefaultAttributes(),
      ...this.globalAttributes,
      ...(exception.attributes ?? {}),
    };

    if (!exception.isMessage && !exception.debugIds) {
      const debugIds = debugIdsForStackTrace(exception.stackTrace);
      if (debugIds) {
        exception.debugIds = debugIds;
      }
    }

    const recorderEvents =
      this.recorder && this.recorder.hasSegments()
        ? this.recorder.getSegments().flatMap((s) => s.events)
        : [];
    const logSnapshot = this.logs.snapshot();
    const actionSnapshot = this.actions.snapshot();
    const hasTimelineData =
      recorderEvents.length > 0 ||
      logSnapshot.length > 0 ||
      actionSnapshot.length > 0;

    // Tag the exception with the parent session id (if always-on is on) so
    // the dashboard can show a "View full session" link. Sessions and the
    // per-exception clip are independent attachments — both ride along. The
    // clip captures the rolling timeline window (default 10 s; bumped to
    // match `sessionRecordingSegmentDuration` when always-on is active).
    if (this.recordAllSessions && this.sessionId) {
      exception.sessionId = this.sessionId;
    }

    if (hasTimelineData) {
      const exceptionId = generateUUID();
      exception.sessionRecordingId = exceptionId;
      const payload: SessionRecordingPayload = {
        exceptionId,
        events: recorderEvents,
      };
      const anchors = computeRecordingAnchors(
        recorderEvents,
        logSnapshot,
        actionSnapshot,
      );
      if (anchors.startedAt) payload.startedAt = anchors.startedAt;
      if (anchors.endedAt) payload.endedAt = anchors.endedAt;
      if (logSnapshot.length > 0) payload.logs = logSnapshot;
      if (actionSnapshot.length > 0) payload.actions = actionSnapshot;
      this.pendingRecordings.push(payload);
    }

    this.pendingExceptions.push(exception);
    this.scheduleSync();
  }

  private shouldIgnore(exception: ExceptionStackTrace): boolean {
    if (this.ignoreErrors.length > 0) {
      const text = exception.stackTrace;
      for (const pattern of this.ignoreErrors) {
        if (typeof pattern === "string") {
          if (text.includes(pattern)) return true;
        } else {
          pattern.lastIndex = 0;
          if (pattern.test(text)) return true;
        }
      }
    }

    if (this.beforeCapture !== null) {
      try {
        const result = this.beforeCapture(exception);
        if (result === false) return true;
      } catch (err) {
        if (this.debug) {
          console.error("Traceway: beforeCapture callback threw:", err);
        }
      }
    }

    return false;
  }

  private scheduleSync(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.doSync();
    }, this.debounceMs);
  }

  private async doSync(): Promise<void> {
    if (this.isSyncing) return;
    if (
      this.pendingExceptions.length === 0 &&
      this.pendingRecordings.length === 0 &&
      this.pendingSessions.length === 0
    ) {
      return;
    }

    this.isSyncing = true;
    const batch = this.pendingExceptions.splice(0);
    const recordings = this.pendingRecordings.splice(0);
    const sessions = this.pendingSessions.splice(0);

    const frame: CollectionFrame = {
      stackTraces: batch,
      metrics: [],
      traces: [],
      sessionRecordings: recordings.length > 0 ? recordings : undefined,
      sessions: sessions.length > 0 ? sessions : undefined,
    };

    const payload: ReportRequest = {
      collectionFrames: [frame],
      appVersion: this.version,
      serverName: "",
    };

    let failed = false;
    try {
      const success = await sendReport(
        this.apiUrl,
        this.token,
        JSON.stringify(payload),
        this.unloading ? { keepalive: true } : undefined,
      );
      if (!success) {
        failed = true;
        this.pendingExceptions.unshift(...batch);
        this.pendingRecordings.unshift(...recordings);
        this.pendingSessions.unshift(...sessions);
        if (this.debug) {
          console.error("Traceway: sync failed, re-queued exceptions");
        }
      }
    } catch (err) {
      failed = true;
      this.pendingExceptions.unshift(...batch);
      this.pendingRecordings.unshift(...recordings);
      this.pendingSessions.unshift(...sessions);
      if (this.debug) {
        console.error("Traceway: sync error:", err);
      }
    } finally {
      this.isSyncing = false;
      if (
        this.pendingExceptions.length > 0 ||
        this.pendingRecordings.length > 0 ||
        this.pendingSessions.length > 0
      ) {
        if (failed) {
          this.scheduleRetry();
        } else {
          this.doSync();
        }
      }
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.doSync();
    }, this.retryDelayMs);
  }

  async flush(timeoutMs?: number): Promise<void> {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.lifecycle) {
      this.lifecycle.uninstall();
    }
    if (this.recordAllSessions) {
      this.endSession();
    }
    if (this.recorder) {
      this.recorder.stop();
    }

    const syncPromise = this.doSync();

    if (timeoutMs !== undefined) {
      await Promise.race([
        syncPromise,
        new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    } else {
      await syncPromise;
    }
  }
}
